import { useMemo, useCallback } from "react";
import {
  ReactFlow,
  Controls,
  Background,
  BackgroundVariant,
  type NodeTypes,
  type NodeMouseHandler,
  Panel,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import { Skeleton, Empty, theme, Typography, Space } from "antd";
import { ApartmentOutlined, TeamOutlined } from "@ant-design/icons";
import { useCustom, useGo } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { DepartmentNode } from "./DepartmentNode";
import { useTreeLayout, type TreeNodeData } from "./useTreeLayout";

const { Text } = Typography;

// Register custom node types
const nodeTypes: NodeTypes = {
  departmentNode: DepartmentNode as unknown as NodeTypes[string],
};

interface OrgChartViewProps {
  resource: string;
  itemTypeName: string;
}

/**
 * Organization chart view for Department items.
 * Fetches all departments and renders them as a tree using React Flow.
 */
export const OrgChartView: React.FC<OrgChartViewProps> = ({
  resource,
  itemTypeName,
}) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const go = useGo();

  // Fetch ALL departments (no pagination) for the tree
  // URL is relative to axiosInstance baseURL (already /api)
  const { data, isLoading, isError } = useCustom({
    url: `items/${itemTypeName}`,
    method: "get",
    config: {
      query: {
        pageSize: 500,
        page: 1,
      },
    },
  });

  const departments = useMemo(() => {
    const items = data?.data;
    if (Array.isArray(items)) return items as Record<string, unknown>[];
    if (items && typeof items === "object" && "data" in items) {
      return (items as { data: Record<string, unknown>[] }).data;
    }
    return [];
  }, [data]);

  // Build tree layout
  const { nodes, edges } = useTreeLayout(departments);

  // Navigate to department detail on node click
  const onNodeClick: NodeMouseHandler = useCallback(
    (_event, node) => {
      go({ to: `/${resource}/${node.id}` });
    },
    [go, resource],
  );

  // Edge styling
  const defaultEdgeOptions = useMemo(
    () => ({
      style: {
        stroke: token.colorBorderSecondary,
        strokeWidth: 2,
      },
      animated: false,
    }),
    [token],
  );

  if (isLoading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  if (isError || departments.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={t("orgChart.noData", "No department data available")}
      />
    );
  }

  // CSS overrides for React Flow controls & minimap to respect dark mode
  const rfStyleOverrides = `
    .orgchart-flow .react-flow__controls {
      background: ${token.colorBgContainer};
      border: 1px solid ${token.colorBorderSecondary};
      border-radius: 8px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    }
    .orgchart-flow .react-flow__controls button {
      background: ${token.colorBgContainer};
      border-bottom: 1px solid ${token.colorBorderSecondary};
      color: ${token.colorText};
      fill: ${token.colorText};
    }
    .orgchart-flow .react-flow__controls button:hover {
      background: ${token.colorBgTextHover};
    }
    .orgchart-flow .react-flow__controls button svg {
      fill: ${token.colorText};
    }
  `;

  return (
    <div
      className="orgchart-flow"
      style={{
        height: "calc(100vh - 280px)",
        minHeight: 500,
        border: `1px solid ${token.colorBorderSecondary}`,
        borderRadius: 8,
        overflow: "hidden",
        background: token.colorBgLayout,
      }}
    >
      <style>{rfStyleOverrides}</style>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodeClick={onNodeClick}
        defaultEdgeOptions={defaultEdgeOptions}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        attributionPosition="bottom-left"
        proOptions={{ hideAttribution: true }}
        nodesDraggable={false}
        nodesConnectable={false}
        elementsSelectable={true}
        panOnDrag
        zoomOnScroll
        minZoom={0.2}
        maxZoom={1.5}
      >
        <Controls showInteractive={false} />
        <Background
          variant={BackgroundVariant.Dots}
          gap={20}
          size={1}
          color={token.colorBorderSecondary}
        />
        {/* Stats panel */}
        <Panel position="top-left">
          <div
            style={{
              background: token.colorBgContainer,
              padding: "8px 12px",
              borderRadius: 8,
              border: `1px solid ${token.colorBorderSecondary}`,
              boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            }}
          >
            <Space size={16}>
              <Space size={4}>
                <ApartmentOutlined style={{ color: token.colorPrimary }} />
                <Text strong style={{ fontSize: 12 }}>
                  {departments.length} {t("orgChart.departments", "departments")}
                </Text>
              </Space>
              <Space size={4}>
                <TeamOutlined style={{ color: token.colorTextSecondary }} />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {nodes.filter((n) => (n.data as TreeNodeData).depth === 0).length}{" "}
                  {t("orgChart.rootDepartments", "root")}
                </Text>
              </Space>
            </Space>
          </div>
        </Panel>
      </ReactFlow>
    </div>
  );
};

export default OrgChartView;
